import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { Copy, Check, Gift, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import ReferralListPanel from './ReferralListPanel';

const ReferEarnModal = ({ isOpen, onClose, onWithdraw, onOpenProfile }) => {
  const { user, getReferralLink } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [referralTab, setReferralTab] = useState('active');
  const [referrals, setReferrals] = useState([]);
  const [summary, setSummary] = useState({ active: 0, inactive: 0, total: 0 });
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const [referralError, setReferralError] = useState('');

  const referralLink = getReferralLink();

  // Reward Eligibility — balance based.
  // First redeem unlocks at 10 reward credits; future redeems unlock at 100.
  // No ₹ amount, no % number, no earnings — visual progress only.
  const referralCountTarget = Math.max(Number(user?.referralCountTarget || user?.rewardRedeemThreshold || 10), 1);
  const referralCountProgress = Math.min(
    Math.max(Number(user?.referralCountProgress || user?.redeemableAmount || 0), 0),
    referralCountTarget,
  );
  const referralCountEligible = Boolean(user?.rewardBalanceEligible ?? user?.referralCountEligible);
  const pendingRedeemExists = Boolean(user?.pendingRedeemExists);
  const hasPreviousReward = Number(user?.rewardRedeemSuccessCount || 0) > 0 || Number(user?.referralRedemptionBaseline || 0) > 0;
  // Once a reward request is submitted, prevent the user from creating a new
  // one until admin processes it (reject → re-enabled, approve → "Previous
  // Reward Done" until next eligibility cycle).
  const canRedeem = referralCountEligible && !pendingRedeemExists;
  const progressBarPercent = Number.isFinite(Number(user?.rewardProgressPerc))
    ? Number(user.rewardProgressPerc)
    : (referralCountProgress / referralCountTarget) * 100;
  useEffect(() => {
    const loadReferrals = async () => {
      if (!isOpen || !user?.id) return;
      try {
        setIsLoadingReferrals(true);
        setReferralError('');
        const { data } = await api.getUserReferrals(user.id);
        setReferrals(Array.isArray(data?.items) ? data.items : []);
        setSummary(data?.summary || { active: 0, inactive: 0, total: 0 });
      } catch (error) {
        setReferralError(error?.response?.data?.detail || error.message || 'Failed to load referrals.');
      } finally {
        setIsLoadingReferrals(false);
      }
    };

    loadReferrals();
  }, [isOpen, user?.id]);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewFullList = () => {
    onClose();
    navigate('/referrals');
  };

  const handleReferralClick = (referral) => {
    const referralUserId = referral?.userId || referral?.id;
    if (!referralUserId || typeof onOpenProfile !== 'function') return;

    // Open the referral user's public profile — not the chat/inbox.
    onClose();
    onOpenProfile(referral);
  };

  const handleRedeem = () => {
    if (!canRedeem) return;
    onClose();
    if (onWithdraw) onWithdraw();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          hideCloseButton
          className="harday-refer-modal glass-panel border-white/40 inset-0 translate-x-0 translate-y-0 w-screen max-w-none rounded-none sm:rounded-none border-0 p-0 gap-0 flex flex-col overflow-hidden"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="harday-refer-modal-close absolute right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-black/70 opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          <div className="harday-refer-modal-scroll flex-1 overflow-y-auto px-6">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl text-[#000000]">
              Refer & Earn
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Reward Eligibility — replaces Total Earned / Available Amount / Withdrawal */}
            <div className="glass-panel rounded-[16px] p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#000000] font-['Outfit']">
                  Reward Eligibility
                </h3>
              </div>

              <div className="mb-2 flex items-center justify-end">
                {pendingRedeemExists ? (
                  <span
                    data-testid="reward-status-pending"
                    className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700"
                  >
                    Reward Pending
                  </span>
                ) : hasPreviousReward ? (
                  <span
                    data-testid="reward-status-previous-done"
                    className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                  >
                    Previous Reward Done
                  </span>
                ) : null}
              </div>

              <div
                data-testid="reward-progress-bar"
                className="h-2.5 rounded-full bg-white/50 overflow-hidden mb-3"
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#F59E0B] transition-all duration-500"
                  style={{ width: `${progressBarPercent}%` }}
                />
              </div>

              <button
                onClick={handleRedeem}
                disabled={!canRedeem}
                data-testid="refer-redeem-btn"
                className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#EF4444] text-white py-3 rounded-full font-semibold flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Gift className="w-4 h-4" />
                Redeem Reward
              </button>
            </div>

            <ReferralListPanel
              items={referrals}
              summary={summary}
              referralTab={referralTab}
              onChangeTab={setReferralTab}
              isLoading={isLoadingReferrals}
              error={referralError}
              previewLimit={4}
              onViewFullList={handleViewFullList}
              onReferralClick={handleReferralClick}
            />

            <div>
              <label className="text-sm text-[#666666] mb-2 block font-['DM_Sans']">Your Referral Link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  data-testid="referral-link-input"
                  className="flex-1 p-3 rounded-[16px] glass-panel text-[#000000] text-sm focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  data-testid="copy-referral-link"
                  className="p-3 rounded-[16px] bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>

          </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReferEarnModal;
